import {
    SearchAufgabenQuery,
    useDeleteAufgabe, useDuplicateAufgabe,
    useSearchAufgaben,
    useStarteTestCall
} from "../../client/hooks";
import {QueryClient, useMutation, useQueryClient} from "react-query";
import {useTranslation} from "react-i18next";
import {Aufgabe, AufgabenListe, TestCallDaten} from "../../api";
import {AufgabenFilter} from "../../components/aufgaben/table/FilterTableAufgaben";
import {useNavigate} from "react-router-dom";
import {permissions} from "../../permissions";
import {useUserMandant} from "../useUserMandant";
import {useLocation} from "react-router";
import {useGetAccessToken} from "../../client";
import {useSnackbar} from "notistack";
import React, {useCallback, useEffect, useState} from "react";
import {apiUrl} from "../../config";
import {capitalize} from "@mui/material";

export const useAufgabenHandling = () => {
    const {t: translation} = useTranslation('aufgaben');
    const {t: translationLanguagesCodes} = useTranslation('sprachen');
    const queryClient: QueryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const {isSystemUser, checkMandantAndHasOneOfPermissions, checkHasOneOfPermissions, isMandantAnrufer} = useUserMandant();
    const getAccessToken = useGetAccessToken();
    const { enqueueSnackbar } = useSnackbar();
    const [loadingExport, setLoadingExport] = useState(false);

    const deleteAufgabe: (aufgabeId: string) => Promise<Aufgabe> = useDeleteAufgabe();
    const testAufgabe: (requestBody: TestCallDaten) => Promise<void> = useStarteTestCall();
    const searchAufgaben: (query: SearchAufgabenQuery) => Promise<AufgabenListe> = useSearchAufgaben();
    const duplicateAufgabe: (aufgabeId: string) => Promise<Aufgabe> = useDuplicateAufgabe();

    const duplicateAufgabeMutation = useMutation(
        (aufgabeId: string): Promise<Aufgabe> => duplicateAufgabe(aufgabeId),
        {
            onSuccess: async (data: Aufgabe, aufgabeId: string) => {
                await queryClient.invalidateQueries(["aufgaben", aufgabeId]);
                navigate("/aufgaben/" + data.id);
                enqueueSnackbar(capitalize(translation('duplicated')), {
                    variant: "success",
                });
            },
            onError: (err: unknown) => {
                enqueueSnackbar(String(err instanceof Error ? err.message : err), { variant: "error" });
            },
        }
    );


    const deleteAufgabeMutation = useMutation(
        (aufgabeId: string): Promise<Aufgabe> => deleteAufgabe(aufgabeId),
        {
            onSuccess: async (_data: Aufgabe, aufgabeId: string) => {
                await queryClient.invalidateQueries(["aufgaben", aufgabeId]);
                navigate("/aufgaben");
            },
        }
    );

    const starteTestCallMutation = useMutation(
        (requestBody: TestCallDaten): Promise<void> => testAufgabe(requestBody),
        {
          onSuccess: async (_data, requestBody) => {
            if (requestBody.aufgabeId) {
              await queryClient.invalidateQueries(["aufgaben", requestBody.aufgabeId]);
            }
            enqueueSnackbar(capitalize(translation('call-info')), {
              variant: "success",
            });
          },
        }
    );

    const loadAufgaben = async (params: any, filter: AufgabenFilter): Promise<AufgabenListe> => {
        const search: string | undefined = filter.search?.toLowerCase();
        const filtered: string | undefined = params.filtered ?? undefined;
        const page: number = params.page ?? 0;
        const limit: number = params.limit ?? 25;
        const sort: string | undefined = params.sort ?? undefined;

        return await searchAufgaben({
            search: search,
            filtered: filtered,
            page: page,
            limit: limit,
            sort: sort,
        })
    }

    const canCreateAufgabeField = () => {
        return (
            isSystemUser ||
            checkHasOneOfPermissions([
                    permissions.aufgaben.create,
                    permissions.aufgaben.own_create,
                ]
            ));
    }

    const canEditAufgabeField = (mandantId: string) => {
        return (
            isSystemUser ||
            checkMandantAndHasOneOfPermissions(mandantId,
                [
                    permissions.aufgaben.edit,
                    permissions.aufgaben.own_edit,
                ]
            ));
    }

    const getAufgabeIdByObject = (aufgabeObject: any) => {
        return typeof aufgabeObject === "string" ? aufgabeObject : (aufgabeObject as Aufgabe)?.id ?? "";
    }

    const tabNames = ["versions", "unteraufgaben", "scheduler", "kontakte"];
    const currentTab = tabNames.find(tab => location.pathname.endsWith(tab)) || (isMandantAnrufer ? "kontakte" : "");

    useEffect(() => {
        if (isMandantAnrufer && !location.pathname.endsWith("kontakte")) {
            const pathParts = location.pathname.split('/');
            const aufgabeId = pathParts[2];
            if (aufgabeId) {
                navigate(`/aufgaben/${aufgabeId}/kontakte`, { replace: true });
            }
        }
    }, [isMandantAnrufer, location.pathname, navigate]);

    const handleTabChange = (_: React.SyntheticEvent, newValue: string, aufgabeId: string | undefined) => {
        const tabBasePath = `/aufgaben/${aufgabeId}`;
        navigate(`${tabBasePath}/${newValue}`);
    };

    const handleExport = useCallback(async (aufgabeId: string) => {
        setLoadingExport(true);
        try {
            const token = await getAccessToken();

            const response = await fetch(`${apiUrl}/api/v1/aufgaben/${aufgabeId}/auswertung-export`, {
                method: 'GET',
                headers: {
                    'Accept': 'text/csv',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `aufgabe-${aufgabeId}-auswertung.csv`);
            document.body.appendChild(link);

            link.click();

            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);

            enqueueSnackbar(capitalize(translation('export-success')), { variant: 'success' });
        } catch (error) {
            console.error('Error downloading CSV:', error);
            enqueueSnackbar(capitalize(translation('export-error')), { variant: 'error' });
        } finally {
            setLoadingExport(false);
        }
    }, [enqueueSnackbar, translation, getAccessToken]);

    return {
        translationLanguagesCodes,
        translation,
        deleteAufgabeMutation,
        starteTestCallMutation,
        duplicateAufgabeMutation,
        currentTab,
        loadingExport,
        handleExport,
        loadAufgaben,
        canEditAufgabeField,
        canCreateAufgabeField,
        getAufgabeIdByObject,
        handleTabChange,
    }
}